import { BackgroundMapUniverseMetric } from '@weenat/client/dist/core/universes'
import { TimeSteps } from '@weenat/client/dist/enums/TimeSteps'
import { Unit } from '@weenat/client/dist/enums/UnitChoices'
import { useConvertedValue } from '@weenat/client/dist/hooks'
import { HydricStressTrend } from '@weenat/client/dist/resources/measurements.type'
import { useIntl } from '@weenat/wintl'
import useDisclosure from 'app/src/hooks/useDisclosure'
import { RestrictionReason } from 'app/src/hooks/useIsAppAccessRestricted'
import Icons from 'app/src/kit/Icons'
import { LockedCarouselValueModal } from 'app/src/kit/LockedFeat'
import TextNumber from 'app/src/kit/TextNumber'
import HydricStressTrendIcon from 'app/src/kit/icons/HydricStressTrendIcon'
import isNil from 'lodash-es/isNil'
import isNumber from 'lodash-es/isNumber'
import { memo } from 'react'
import { useTheme } from 'styled-components'
import { METRIC_COLUMN_GAP, METRIC_COLUMN_WIDTH } from '../PlotOrStationList'
import { PlotListItemsVariants } from './PlotListItemMetricSummary'
import PlotListItemValueName from './PlotListItemValueName'

export type PlotSummaryValue = string | number | null | undefined
interface PlotListItemValueProps {
  /**
   * @default false
   */
  isMainValue?: boolean
  value: PlotSummaryValue
  metric: BackgroundMapUniverseMetric
  timeStep?: TimeSteps
  name?: string
  selected?: boolean
  className?: string
  /**
   * @default false
   */
  isRestricted?: boolean
  reason?: RestrictionReason
  /** To convert to a specific unit */
  displayedUnit?: Unit
  variant: PlotListItemsVariants
  thresholdColor?: string
  trend?: HydricStressTrend
}

const PlotListItemValue: FC<PlotListItemValueProps> = ({
  metric,
  isMainValue = false,
  name,
  value,
  selected,
  className,
  isRestricted = false,
  reason,
  displayedUnit,
  variant,
  thresholdColor,
  trend
}) => {
  const { colors, radiuses, spacings } = useTheme()
  const { convertValue, getUnit } = useConvertedValue()
  const { t } = useIntl()

  const { close, isOpen, open } = useDisclosure()

  const unit = displayedUnit ?? getUnit(metric)
  const color = isRestricted ? colors.grayscale[300] : colors.metrics[metric]['500']
  const tooltipStyle: React.CSSProperties = {
    border: `1px solid ${selected ? color : 'transparent'}`,
    borderRadius: radiuses.md,
    padding: spacings.md
  } as const

  const listStyle: React.CSSProperties = {
    border: 0,
    padding: spacings.none,
    marginLeft: METRIC_COLUMN_GAP
  } as const

  const finalValue =
    !isNil(value) && isNumber(value) ? convertValue({ metric, displayUnit: false, value }) : null

  const displayedValue = `${!isNil(finalValue) ? finalValue : '-'} ${t(`units.${unit}_abbr`)}`

  const isList = variant === 'list'
  const selectedStyle = isList ? listStyle : tooltipStyle
  const valueSize = isList ? 'rg' : isMainValue ? 'md' : 'rg'
  const valueWeight = isMainValue ? 'bold' : 'regular'

  return (
    <>
      <Flex
        $flexDirection='column'
        $flex={isList ? undefined : 1}
        $justifyContent={!isNil(name) ? 'space-between' : 'flex-end'}
        $alignItems='flex-start'
        style={selectedStyle}
        className={className}
        onClick={
          isRestricted
            ? (e) => {
                e.stopPropagation()
                e.preventDefault()
                open()
              }
            : undefined
        }
      >
        {isNil(name) || isList ? null : <PlotListItemValueName name={name} color={color} />}
        <Flex
          $flexDirection='column'
          $alignItems={isList ? 'flex-end' : 'flex-start'}
          $justifyContent={isList ? 'center' : 'flex-end'}
          $minWidth={isList ? 'min-content' : 'max-content'}
          $width={isList ? METRIC_COLUMN_WIDTH : '100%'}
          $mt={!isNil(name) ? 'sm' : 'auto'}
        >
          {isRestricted ? (
            <Flex $alignItems='flex-end' $flex={1}>
              <Icons.Lock $size='lg' $color={'grayscale.700'} />
            </Flex>
          ) : (
            <Flex $alignItems='center' $justifyContent='center' $gap='md'>
              <TextNumber
                $fontSize={valueSize}
                $fontWeight={valueWeight}
                $textAlign={isList ? 'end' : 'start'}
                $whiteSpace='nowrap'
              >
                {displayedValue}
              </TextNumber>
              {!isNil(thresholdColor) ? (
                <Box $width={18} $height={18} $borderRadius='sm' $backgroundColor={thresholdColor}>
                  {!isNil(trend) && (
                    <HydricStressTrendIcon trend={trend} $color='grayscale.white' $size='md' />
                  )}
                </Box>
              ) : null}
            </Flex>
          )}
        </Flex>
      </Flex>
      {isRestricted && (
        <LockedCarouselValueModal close={close} isOpen={isOpen} reason={reason ?? 'freemium'} />
      )}
    </>
  )
}

export default memo(PlotListItemValue)
